import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrapper } from '../wrapper.styles';
import ProgramSelector from '@/components/ProgramSelector/ProgramSelector';
import AvailablePrograms from '@/components/Group/AvailablePrograms';
import { NavigationButton, PageOrigin } from '@components/Button/NavigationButton/NavigationButton';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { setIsAtLeastOneRepriceDone } from '@/reducers/VmoV2';
import { setSkippedVMO } from '@/reducers/currentPage';
import { VmoFootnote } from '@/components/VmoFootnote/VmoFootnote';
import { handleProgramSelection } from '@/utils/handleVmo';
import { handleRequestVmo } from '@/utils/handleEndpoints';

export interface VmoProps {
  onAccess: (page: string) => void;
}

const Vmo: React.FC<VmoProps> = ({ onAccess }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  const isICE = templateConfig?.isICE ?? false;

  const handleNext = () => {
    dispatch(setIsAtLeastOneRepriceDone(false));
  };

  const autoSkipProcessedRef = useRef(false);

  const singleProgramInfo = useMemo(() => {
    const available = loanApplication?.availablePrograms || {};
    if (isICE) {
      const hel = available?.helRates;
      const heloc = available?.helocRates;
      const types: Array<{ key: 'helRates' | 'helocRates'; list: unknown[] }> = [];
      if (Array.isArray(hel) && hel.length > 0) types.push({ key: 'helRates', list: hel });
      if (Array.isArray(heloc) && heloc.length > 0) types.push({ key: 'helocRates', list: heloc });
      if (types.length === 1 && types[0].list.length === 1) {
        return { programKey: types[0].key, program: types[0].list[0] } as const;
      }
    } else {
      const helData = available?.helData;
      const helocData = available?.helocData;
      const helList = (helData?.rates ?? helData) as unknown[];
      const helocList = (helocData?.rates ?? helocData) as unknown[];
      const candidates: Array<{ key: 'helData' | 'helocData'; list: unknown[] }> = [];
      if (Array.isArray(helList) && helList.length > 0) candidates.push({ key: 'helData', list: helList });
      if (Array.isArray(helocList) && helocList.length > 0) candidates.push({ key: 'helocData', list: helocList });
      if (candidates.length === 1 && candidates[0].list.length === 1) {
        return { programKey: candidates[0].key, program: candidates[0].list[0] } as const;
      }
    }
    return null;
  }, [loanApplication?.availablePrograms, isICE]);

  useEffect(() => {
    const processedRef = autoSkipProcessedRef.current;
    if (processedRef) return;
    if (!singleProgramInfo) return;

    const hasSelectedProgram = loanApplication?.selectedProgram && Object.keys(loanApplication.selectedProgram).length > 0;
    const isHelProgram = singleProgramInfo.programKey === 'helRates' || singleProgramInfo.programKey === 'helData';
    const selectedProgramType = isHelProgram ? 'Home Equity Loan' : 'Line of Credit';

    const autoNavigate = async () => {
      if (!hasSelectedProgram) {
        const auxLoanApplication = { ...loanApplication, selectedProgramType } as typeof loanApplication;
        const finalLoanApplication = handleProgramSelection(singleProgramInfo.program as any, auxLoanApplication as any);

        dispatch({
          type: 'LoanApplication/updateValue',
          payload: { ...finalLoanApplication },
        });

        try {
          await handleRequestVmo(isICE, finalLoanApplication as any, dispatch, templateConfig as any);
        } catch (_) {}
      }

      dispatch(setSkippedVMO(true));
      autoSkipProcessedRef.current = true;
      navigate(`/${PageOrigin.VMO_LINE_AMOUNT}`);
    };

    autoNavigate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleProgramInfo, loanApplication?.selectedProgram]);

  return (
    <>
      <Wrapper $gap="0rem">
        <ProgramSelector />
        <AvailablePrograms />
      </Wrapper>
      <VmoFootnote />
      <NavigationButton
        variant="primary"
        nextButton={templateConfig?.pages?.vmo?.NavigationButton?.nextButton || 'Next'}
        isBackButton={templateConfig?.pages?.vmo?.NavigationButton?.backButton?.isHidden || false}
        backButton={templateConfig?.pages?.vmo?.NavigationButton?.backButton?.text || 'Back'}
        disabled={Object.keys(loanApplication.selectedProgram).length === 0}
        type="next"
        onAccess={onAccess}
        navigation={navigate}
        pageOrigin={PageOrigin.ESTIMATED_AVAILABLE_EQUITY}
        currentPage={PageOrigin.VMO}
        destinationPage={PageOrigin.VMO_LINE_AMOUNT}
        onNext={handleNext}
      />
    </>
  );
};

export default Vmo;
